import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Heart, Sparkles, LayoutDashboard, Users, CalendarCheck, FileText, CheckCircle, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

const LandingPage = () => {
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);

  const features = [
    {
      emoji: "🎙️",
      title: "Dictée vocale",
      subtitle: "Parlez, Carelio écoute.",
      description: "Dictez vos observations entre deux activités, dans la voiture, à la pause. Carelio transcrit fidèlement votre voix en texte structuré."
    },
    {
      emoji: "✨",
      title: "Rédaction assistée par IA",
      subtitle: "Vos faits, votre style, en mieux.",
      description: "À partir de vos notes brutes, l'IA génère un compte rendu clair, structuré et professionnel. Vous gardez la main : vous relisez, vous ajustez, vous validez."
    },
    {
      emoji: "📁",
      title: "Notes & dossiers",
      subtitle: "Tout au même endroit.",
      description: "Créez, organisez et retrouvez vos notes en quelques clics. Une interface simple, sans usine à gaz. Conçue pour des éducateurs, pas pour des informaticiens."
    },
    {
      emoji: "🧩",
      title: "Activités & ressources",
      subtitle: "Construisez votre boîte à outils.",
      description: "Créez vos propres fiches d'activités et accédez à une bibliothèque de ressources éducatives. Partagez ce qui marche, gardez ce qui vous est utile."
    }
  ];

  const testimonials = [
    {
      name: "Marie Dubois",
      role: "Directrice",
      organization: "ESAT Les Lilas",
      text: "Carelio nous a permis de gagner 40% de temps sur la documentation administrative. Notre équipe peut enfin se concentrer sur l'accompagnement."
    },
    {
      name: "Jean Martin",
      role: "Éducateur Spécialisé",
      organization: "IME Saint-Michel",
      text: "L'assistant IA est impressionnant ! Il génère des notes professionnelles qui respectent parfaitement les standards du secteur."
    },
    {
      name: "Sophie Lefèvre",
      role: "Responsable SESSAD",
      organization: "SESSAD Nouvelle Vie",
      text: "La conformité n'a jamais été aussi simple. Le tableau de bord nous donne une vision claire et instantanée de notre situation."
    },
    {
      name: "Thomas Rousseau",
      role: "Directeur",
      organization: "MAS Les Jardins",
      text: "Carelio nous a permis de centraliser toutes nos communications avec les familles. Un gain de temps considérable pour notre MAS."
    },
    {
      name: "Claire Moreau",
      role: "Coordinatrice",
      organization: "FAM Le Soleil",
      text: "La messagerie intégrée facilite énormément les échanges avec les proches de nos résidents. Un outil indispensable pour notre FAM."
    },
    {
      name: "Antoine Bernard",
      role: "Chef de service",
      organization: "ESAT Horizon",
      text: "La dictée vocale change tout. Entre deux accompagnements, je dicte mes observations et le rapport est prêt en quelques secondes."
    },
    {
      name: "Nathalie Petit",
      role: "Psychologue",
      organization: "IME Les Cèdres",
      text: "Avoir tous les suivis centralisés sur une seule plateforme nous permet une bien meilleure coordination d'équipe au quotidien."
    },
    {
      name: "Philippe Durand",
      role: "Directeur adjoint",
      organization: "MAS Belle Étoile",
      text: "Nous avons réduit de moitié le temps consacré aux tâches administratives. Carelio est devenu un outil essentiel pour notre établissement."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FDFBF7] via-[#F0F7F4] to-[#FCF0EC]">

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#FDFBF7]/80 backdrop-blur-md border-b border-slate-100/50">
        <div className="max-w-7xl mx-auto px-4 md:px-12 py-3 md:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Heart className="w-7 h-7 md:w-8 md:h-8 text-primary fill-primary" />
            <span className="text-xl md:text-2xl font-extrabold font-heading text-primary-900">Carelio</span>
          </div>
          <Button 
            data-testid="nav-login-btn" 
            onClick={() => navigate('/auth')} 
            variant="outline" 
            size="sm"
          >
            Connexion
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-[80px] md:pt-[120px] pb-12 md:pb-20 px-4 md:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-3xl sm:text-4xl md:text-6xl font-extrabold tracking-tight text-primary-900 font-heading leading-tight mb-4 md:mb-6">
              Concentrez-vous sur l'humain,<br />Carelio gère le reste.
            </h1>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed mb-8 max-w-2xl mx-auto">
              L'assistant de rédaction pensé pour les éducateurs spécialisés et les professionnels de l'accompagnement. Dictez ce que vous avez observé, Carelio structure vos écrits professionnels. Vous récupérez du temps pour ce qui compte vraiment : les personnes que vous accompagnez.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                data-testid="hero-get-started-btn" 
                onClick={() => navigate('/auth')} 
                size="lg"
              >
                Essayer gratuitement →
              </Button>
              <p className="text-sm text-slate-500">
                Gratuit pendant la bêta · Sans carte bancaire · Connexion en 10 secondes
              </p>
            </div>
          </motion.div>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-12 md:mt-16 rounded-3xl overflow-hidden shadow-2xl max-w-4xl mx-auto"
          >
            <img 
              src="https://images.unsplash.com/photo-1723433892471-62f113c8c9a0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxkaXNhYmlsaXR5JTIwc3VwcG9ydCUyMHdvcmtlciUyMGhlbHBpbmclMjBjbGllbnR8ZW58MHx8fHwxNzc0MjU5NzY2fDA&ixlib=rb-4.1.0&q=85"
              alt="Support professionnel"
              className="w-full object-contain rounded-2xl"
            />
          </motion.div>
        </div>
      </section>

      {/* Comment ça marche */}
      <section id="comment-ca-marche" className="py-12 md:py-20 px-4 md:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-primary-900 font-heading mb-4">
              De l'observation au compte rendu, en 3 étapes
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                num: "1",
                title: "Vous dictez",
                desc: "Vous racontez à voix haute ce que vous avez observé. En une phrase ou en cinq minutes, peu importe."
              },
              {
                num: "2",
                title: "Carelio structure",
                desc: "L'IA transforme votre dictée en compte rendu professionnel, dans le ton et le vocabulaire du secteur médico-social."
              },
              {
                num: "3",
                title: "Vous validez",
                desc: "Vous relisez, vous ajustez si besoin, vous exportez. Et vous rentrez chez vous à l'heure."
              }
            ].map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-12 h-12 rounded-full bg-primary text-white text-xl font-bold flex items-center justify-center mx-auto mb-4">
                  {step.num}
                </div>
                <h3 className="text-xl font-bold text-primary-900 font-heading mb-2">{step.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="py-12 md:py-20 px-4 md:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-4xl font-bold tracking-tight text-primary-900 font-heading mb-4">
              Vous connaissez cette sensation ?
            </h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-6">
            <div className="flex items-start gap-4 p-5 bg-slate-50 rounded-2xl">
              <span className="text-2xl shrink-0">🕐</span>
              <div>
                <h3 className="font-bold text-primary-900 mb-1">La paperasse qui s'accumule</h3>
                <p className="text-sm text-slate-600">Vous finissez vos journées avec 1 à 2 heures d'écrits à rattraper. Vos soirées y passent.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-5 bg-slate-50 rounded-2xl">
              <span className="text-2xl shrink-0">😮‍💨</span>
              <div>
                <h3 className="font-bold text-primary-900 mb-1">Le syndrome de la page blanche</h3>
                <p className="text-sm text-slate-600">Vous savez ce que vous avez observé, mais le mettre en mots clairs et professionnels, c'est une autre histoire.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-5 bg-slate-50 rounded-2xl">
              <span className="text-2xl shrink-0">📚</span>
              <div>
                <h3 className="font-bold text-primary-900 mb-1">Les transmissions qui s'éparpillent</h3>
                <p className="text-sm text-slate-600">Notes sur post-it, dans des carnets, dans votre tête. Au moment de rédiger le bilan, il faut tout rassembler.</p>
              </div>
            </div>
            <p className="text-center text-base md:text-lg font-medium text-primary-900 pt-4">
              Carelio est né de ce constat. Pour vous redonner du temps et de la clarté.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 md:py-20 px-4 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div id="fonctionnalites" className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-primary-900 font-heading mb-4">
              Rédiger ses écrits, simplement
            </h2>
            <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
              Des outils pensés par et pour le terrain. Pas de menus complexes, pas de formation longue : vous êtes opérationnel en 5 minutes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="hover:-translate-y-1 h-full">
                  <div className="flex items-start gap-4">
                    <span className="text-3xl shrink-0">{feature.emoji}</span>
                    <div>
                      <h3 className="text-xl font-bold text-primary-900 font-heading mb-1">
                        {feature.title}
                      </h3>
                      <p className="text-sm font-semibold text-secondary mb-2">{feature.subtitle}</p>
                      <p className="text-slate-600 leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pour qui Section */}
      <section className="py-12 md:py-16 px-4 md:px-12 bg-gradient-to-br from-secondary-50 to-primary-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-4xl font-bold tracking-tight text-primary-900 font-heading mb-4">
              Conçu pour les professionnels de l'accompagnement
            </h2>
            <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
              Que vous travailliez en établissement, en service à domicile ou en milieu ouvert, Carelio s'adapte à votre quotidien.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              'Éducateurs spécialisés',
              'Moniteurs-éducateurs',
              'AES & AMP',
              'Travailleurs sociaux',
              'Coordinateurs & chefs de service',
              'Équipes éducatives'
            ].map((profil, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-6 text-center hover:shadow-xl transition-all hover:-translate-y-1"
              >
                <span className="text-2xl mb-2 block">👤</span>
                <h3 className="text-sm font-bold text-primary-900 leading-tight">
                  {profil}
                </h3>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-sm text-slate-600 mt-8 max-w-3xl mx-auto">
            Que vous interveniez en IME, SESSAD, MAS, FAM, ESAT, foyer de vie, association ou en libéral — Carelio s'utilise partout où l'on rédige des écrits professionnels.
          </p>
        </div>
      </section>

      {/* Beta Section */}
      <section className="py-12 md:py-20 px-4 md:px-12">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Card>
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-primary-900 font-heading mb-3">
                  Vous serez parmi les premiers
                </h2>
                <p className="text-base text-slate-600">
                  Carelio est en bêta. Les premiers éducateurs qui rejoignent l'aventure bénéficient :
                </p>
              </div>
              <div className="space-y-4 mb-8">
                {[
                  "D'un accès gratuit illimité pendant toute la phase bêta",
                  "D'un lien direct avec l'équipe pour faire remonter vos besoins",
                  "De voir leurs retours intégrés dans les prochaines versions",
                  "De conserver des conditions privilégiées après la sortie officielle"
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="text-secondary font-bold shrink-0">✓</span>
                    <p className="text-slate-700">{item}</p>
                  </div>
                ))}
              </div>
              <div className="text-center">
                <Button
                  data-testid="beta-cta-btn"
                  onClick={() => navigate('/auth')}
                  size="lg"
                  className="text-base md:text-lg px-8 py-4"
                >
                  Rejoindre la bêta →
                </Button>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Transparence Section */}
      <section className="py-12 md:py-20 px-4 md:px-12">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-primary-900 font-heading">
              Soyons transparents
            </h2>
          </div>
          <div className="space-y-6">
            <div className="flex items-start gap-4 p-6 bg-slate-50 rounded-2xl">
              <span className="text-2xl shrink-0">🚧</span>
              <div>
                <h3 className="font-bold text-primary-900 mb-1">Carelio est en construction</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Nous sommes en phase bêta. Notre objectif est de bâtir, avec vous, l'outil que vous auriez aimé avoir depuis longtemps.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-6 bg-slate-50 rounded-2xl">
              <span className="text-2xl shrink-0">🔐</span>
              <div>
                <h3 className="font-bold text-primary-900 mb-1">Sur vos données</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Pendant la phase bêta, nous recommandons de ne pas saisir d'informations nominatives sur les personnes accompagnées. Utilisez des initiales ou des prénoms d'usage (« M. T. », « Léa »). La certification HDS et la conformité RGPD complète sont sur notre feuille de route pour 2026.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-6 bg-slate-50 rounded-2xl">
              <span className="text-2xl shrink-0">💬</span>
              <div>
                <h3 className="font-bold text-primary-900 mb-1">Une question ? Une idée ?</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Nous lisons chaque message. Écrivez-nous à <a href="mailto:contact@carelio.fr" className="font-semibold text-primary underline">contact@carelio.fr</a>, on vous répond personnellement.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-12 md:py-20 px-4 md:px-12">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-primary-900 font-heading">
              Vos questions, nos réponses
            </h2>
          </div>
          <div className="space-y-4">
            {[
              {
                q: "Combien ça coûte ?",
                a: "Carelio est gratuit pendant toute la phase bêta. Quand nous lancerons une version payante, les utilisateurs bêta bénéficieront de conditions privilégiées."
              },
              {
                q: "Mes écrits sont-ils en sécurité ?",
                a: "Vos données sont stockées de manière sécurisée et ne sont jamais utilisées pour entraîner des modèles d'IA tiers. Cela dit, nous sommes en phase bêta : nous recommandons de ne pas saisir d'informations nominatives sur les personnes accompagnées pour le moment."
              },
              {
                q: "Faut-il une formation pour utiliser Carelio ?",
                a: "Non. Si vous savez utiliser un téléphone, vous savez utiliser Carelio. La prise en main se fait en 5 minutes."
              },
              {
                q: "Est-ce que ça marche pour tous les types d'écrits ?",
                a: "Carelio est particulièrement adapté aux observations, transmissions, comptes rendus d'activité, synthèses de réunion et bilans éducatifs. Pour les projets personnalisés et écrits longs structurés, c'est en cours d'amélioration."
              },
              {
                q: "Qui est derrière Carelio ?",
                a: "Carelio est porté par une équipe convaincue que les éducateurs méritent de meilleurs outils. Une question ? Écrivez-nous, vous tomberez directement sur nous."
              },
              {
                q: "Comment rejoindre la bêta ?",
                a: "Cliquez sur \"Essayer gratuitement\", connectez-vous avec votre compte Google, et c'est parti. Aucune carte bancaire demandée."
              }
            ].map((item, index) => (
              <details key={index} className="group bg-slate-50 rounded-2xl">
                <summary className="flex items-center justify-between cursor-pointer p-5 font-bold text-primary-900 select-none">
                  <span>{item.q}</span>
                  <span className="text-primary transition-transform group-open:rotate-45 text-xl leading-none">+</span>
                </summary>
                <p className="px-5 pb-5 text-sm text-slate-600 leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-20 px-4 md:px-12 bg-gradient-to-br from-primary-50 to-secondary-50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-primary-900 font-heading mb-4">
              Reprenez du temps pour l'essentiel.
            </h2>
            <p className="text-base md:text-lg text-slate-600 mb-8">
              Rejoignez les premiers éducateurs qui ont décidé de ne plus passer leurs soirées sur leurs écrits professionnels.
            </p>
            <Button 
              data-testid="cta-get-started-btn" 
              onClick={() => navigate('/auth')} 
              size="lg"
              className="text-base md:text-lg px-8 py-4"
            >
              Essayer Carelio gratuitement →
            </Button>
            <p className="text-sm text-slate-500 mt-4">
              Sans carte bancaire · Sans engagement · Connexion Google en 10 secondes
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-100 py-8 md:py-12 px-4 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Heart className="w-6 h-6 text-primary fill-primary" />
                <span className="text-xl font-bold font-heading text-primary-900">Carelio</span>
              </div>
              <p className="text-slate-600 text-sm">
                L'assistant de rédaction pour les professionnels de l'accompagnement. Conçu avec et pour les éducateurs, en France.
              </p>
            </div>
            
            <div>
              <h3 className="font-bold text-slate-900 mb-4">Produit</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="#fonctionnalites" className="text-slate-600 hover:text-primary transition-colors">Fonctionnalités</a></li>
                <li><a href="#comment-ca-marche" className="text-slate-600 hover:text-primary transition-colors">Comment ça marche</a></li>
                <li><a href="/dashboard/content" className="text-slate-600 hover:text-primary transition-colors">Contenu éducatif</a></li>
                <li><a href="#faq" className="text-slate-600 hover:text-primary transition-colors">FAQ</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-bold text-slate-900 mb-4">Légal</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="/mentions-legales" className="text-slate-600 hover:text-primary transition-colors">Mentions légales</a></li>
                <li><a href="/confidentialite" className="text-slate-600 hover:text-primary transition-colors">Politique de confidentialité</a></li>
                <li><a href="/cgu" className="text-slate-600 hover:text-primary transition-colors">CGU</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-bold text-slate-900 mb-4">Contact</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <a href="mailto:contact@carelio.fr" className="text-slate-600 hover:text-primary transition-colors">
                    📧 contact@carelio.fr
                  </a>
                </li>
                <li className="text-slate-500">Nous répondons sous 24h, en personne.</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-100 pt-6 text-center">
            <p className="text-slate-500 text-sm">
              © 2026 Carelio · Conçu en France pour les éducateurs · Plateforme en bêta
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
